import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { apiClient } from '@api/client';
import { useNotificationStore } from '@store/notificationStore';
import type {
  CreatePaymentOrderRequest,
  PaymentOrder,
  PaymentOrderListResponse,
  StripeConnection,
  TenantFundingProfile,
  TenantFundingPaymentMethod,
  TenantFundingPaymentMethodListResponse,
  VendorPaymentProfile,
  VendorPaymentProfileListResponse,
} from '@/types/payments';

export function useStripeConnection(enabled = true) {
  return useQuery({
    queryKey: ['payments', 'stripe', 'connection'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<StripeConnection>('/payments/providers/stripe/connection');
      return data;
    },
  });
}

export function useStartStripeConnection() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { return_path?: string; force_reconnect?: boolean; country?: string }) => {
      const { data } = await apiClient.post<StripeConnection>('/payments/providers/stripe/connection/start', payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'stripe', 'connection'] });
      addNotification({
        type: 'success',
        message: data.onboarding_url ? 'Stripe onboarding started.' : 'Stripe connection initialized.',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to start Stripe connection.',
      });
    },
  });
}

export function useRefreshStripeConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<StripeConnection>('/payments/providers/stripe/connection/refresh');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'stripe', 'connection'] });
    },
  });
}

export function useTenantFundingProfile() {
  return useQuery({
    queryKey: ['payments', 'tenant-funding'],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantFundingProfile>('/payments/tenant-funding');
      return data;
    },
  });
}

export function useStartTenantFundingSetupIntent() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload?: { email?: string; name?: string; payment_method_types?: Array<'card' | 'us_bank_account'> }) => {
      const { data } = await apiClient.post<TenantFundingProfile>('/payments/tenant-funding/setup-intent', payload ?? {});
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'tenant-funding'] });
      addNotification({
        type: 'success',
        message: 'Tenant funding setup started. Complete setup and confirm payment method.',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to start tenant funding setup.',
      });
    },
  });
}

export function useConfirmTenantFundingMethod() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { payment_method_id: string; stripe_customer_id?: string }) => {
      const { data } = await apiClient.post<TenantFundingProfile>('/payments/tenant-funding/confirm', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'tenant-funding'] });
      queryClient.invalidateQueries({ queryKey: ['payments', 'tenant-funding', 'payment-methods'] });
      addNotification({
        type: 'success',
        message: 'Tenant funding method confirmed.',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to confirm tenant funding method.',
      });
    },
  });
}

export function useTenantFundingPaymentMethods() {
  return useQuery({
    queryKey: ['payments', 'tenant-funding', 'payment-methods'],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantFundingPaymentMethodListResponse>('/payments/tenant-funding/payment-methods');
      return data.items;
    },
  });
}

export function useVendorPaymentProfiles(enabled = true) {
  return useQuery({
    queryKey: ['payments', 'vendor-profiles'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<VendorPaymentProfileListResponse>('/payments/vendor-profiles');
      return data.items;
    },
  });
}

export function useStartVendorPaymentProfile() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      vendorId,
      payload,
    }: {
      vendorId: string;
      payload: { return_path?: string; email?: string; country?: string; currency?: string };
    }) => {
      const { data } = await apiClient.post<VendorPaymentProfile>(
        `/payments/vendors/${vendorId}/payment-profile/start`,
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'vendor-profiles'] });
      addNotification({
        type: 'success',
        message: data.status === 'ready' ? 'Vendor payout account is already linked.' : 'Vendor payout setup started.',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to start vendor payout setup.',
      });
    },
  });
}

export function useRefreshVendorPaymentProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorId: string) => {
      const { data } = await apiClient.get<VendorPaymentProfile>(`/payments/vendors/${vendorId}/payment-profile/refresh`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'vendor-profiles'] });
    },
  });
}

export function usePaymentOrders(sourceType: 'ap_posting' = 'ap_posting', enabled = true) {
  return useQuery({
    queryKey: ['payments', 'orders', sourceType ?? 'all'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentOrderListResponse>('/payments/orders', {
        params: sourceType ? { source_type: sourceType } : undefined,
      });
      return data.items;
    },
  });
}

export function useCreatePaymentOrder() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreatePaymentOrderRequest) => {
      const { data } = await apiClient.post<PaymentOrder>('/payments/orders', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'orders'] });
      addNotification({ type: 'success', message: 'Payment order created.' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to create payment order.',
      });
    },
  });
}

export function useSubmitPaymentOrder() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (paymentOrderId: string) => {
      const { data } = await apiClient.post<PaymentOrder>(`/payments/orders/${paymentOrderId}/submit`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'orders'] });
      addNotification({ type: 'success', message: 'Payment submitted to Stripe.' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit payment order.',
      });
    },
  });
}
